// Settings → General: catering on the Kitchen Display (kitchen flow, migration
// 149). Admin only, and only with kitchen_flow on — renders nothing otherwise,
// so live tenants see no new setting. Off by default.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useAllSettings, useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { isKDSEnabled, useKitchenSettings } from '@/hooks/useKitchenSettings'
import { useToast } from '@/hooks/use-toast'
import { CK } from '@/lib/cateringKds/cateringKdsStrings'
import { CATERING_KDS_KEY, MAX_DAYS_BEFORE, parseCateringOnKds } from '@/lib/cateringKds/cateringKdsLogic'
import type { CateringOnKdsSetting } from '@/lib/cateringKds/cateringKdsTypes'
import { getStoredUserRole } from '@/lib/staff-roles'
import { plainError } from '@/components/store/inventory/prep/prepRecipeDraft'

export function CateringOnKdsSettingCard() {
  const enabled = useKitchenFlowEnabled()
  const all = useAllSettings()
  const kitchen = useKitchenSettings()
  const current = parseCateringOnKds(all)
  const qc = useQueryClient()
  const { toast } = useToast()
  const save = useMutation({
    mutationFn: (next: CateringOnKdsSetting) => apiClient.updateSetting(CATERING_KDS_KEY, next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'all'] })
      toast({ title: CK.settingSaved })
    },
    onError: (e: Error) => toast({ variant: 'destructive', title: plainError(e) }),
  })
  if (!enabled || getStoredUserRole() !== 'admin') return null

  // While a change is in flight the controls show where they are going.
  const shown = save.isPending && save.variables ? save.variables : current
  const set = (patch: Partial<CateringOnKdsSetting>) => save.mutate({ ...shown, ...patch })

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">{CK.settingTitle}</CardTitle>
        <CardDescription>{CK.settingHelp}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3">
          <Switch id="catering-kds-enabled" checked={shown.enabled} disabled={save.isPending} onCheckedChange={(v) => set({ enabled: v })} />
          <Label htmlFor="catering-kds-enabled">{CK.settingSwitch}</Label>
        </div>
        {shown.enabled && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="catering-kds-days" className="text-sm">{CK.settingDays}</Label>
              <select
                id="catering-kds-days"
                className="flex h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={shown.show_days_before}
                disabled={save.isPending}
                onChange={(e) => set({ show_days_before: Number(e.target.value) })}
              >
                {Array.from({ length: MAX_DAYS_BEFORE + 1 }, (_, n) => (
                  <option key={n} value={n}>{CK.settingDaysOption(n)}</option>
                ))}
              </select>
            </div>
            <p className="text-sm text-muted-foreground">{CK.settingOnNote}</p>
            {!isKDSEnabled(kitchen.mode) && (
              <p className="rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-sm text-warning-fg">{CK.settingKotOnly}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
